import React, { useState, useEffect, useCallback } from "react";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { FAQS } from "../lib/content";
import { trackConversion } from "../lib/analytics";
import { getAttribution } from "../lib/attribution";
import FAQ from "../components/site/FAQ";
import SkylineDivider from "../components/site/SkylineDivider";
import ServiceMarker from "../components/site/ServiceMarker";
import PhoneField, { isValidPhoneNumber } from "../components/site/PhoneField";
import CountryField from "../components/site/CountryField";
import CalEmbed from "../components/site/CalEmbed";
import BookingConfirmation from "../components/site/BookingConfirmation";
import "react-phone-number-input/style.css";

// Three questions. Question 1 doubles as the pathway selector — the
// "partner" option routes to the partnership enquiry instead.
const STEPS = [
  {
    key: "stage",
    title: "Where are you in your Saudi journey?",
    type: "single",
    options: [
      { id: "exploring",      label: "Exploring the market",            sub: "We want to understand whether the Kingdom is right for us." },
      { id: "developing",     label: "Already in conversations",        sub: "Buyers, partners, or strategic accounts are moving." },
      { id: "ready_to_build", label: "Ready to build",                  sub: "We need setup, providers, hiring, or operating support." },
      { id: "active",         label: "Already active, need local coverage", sub: "We want someone on the ground keeping the market alive." },
      { id: "partner",        label: "We want to partner with HuddleUp", sub: "We are a provider, advisor, or ecosystem partner." },
    ],
  },
  {
    key: "objectives",
    title: "What do you need most right now?",
    subtitle: "Select all that apply",
    type: "multi",
    options: [
      { id: "validate_demand",   label: "Validate demand and timing" },
      { id: "pipeline_meetings", label: "Qualified meetings and pipeline" },
      { id: "represented",       label: "Local representation and follow through" },
      { id: "strategic",         label: "Support on a strategic opportunity or tender" },
      { id: "setup_providers",   label: "Provider, pricing, and setup management" },
      { id: "hire_train",        label: "Hiring or building Saudi sales capability" },
    ],
  },
  {
    key: "timing",
    title: "When are you looking to move?",
    type: "single",
    options: [
      { id: "now",             label: "Now" },
      { id: "3_months",        label: "Within 3 months" },
      { id: "6_months",        label: "Within 6 months" },
      { id: "later_this_year", label: "Later this year" },
      { id: "not_sure",        label: "Not sure yet" },
    ],
  },
];

const labelFor = (stepKey, id) =>
  STEPS.find((s) => s.key === stepKey)?.options.find((o) => o.id === id)?.label || id;

const initialPartner = {
  company_name: "",
  website: "",
  contact_name: "",
  email: "",
  phone: "",
  phone_country: "AE",
  phone_country_name: "United Arab Emirates",
  phone_country_code: "+971",
  country: "",
  country_name: "",
  partner_type: "",
  services: "",
  markets: "",
  supports_saudi: "",
  notes: "",
};

const STORAGE_KEY = "huddleup:diagnostic:v3";

// ---------- Validators ----------
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isUrl = (v) => {
  if (!v) return false;
  try {
    const url = new URL(v.startsWith("http") ? v : `https://${v}`);
    return !!url.hostname && url.hostname.includes(".");
  } catch {
    return false;
  }
};

export default function Diagnostic() {
  const [phase, setPhase] = useState("client"); // client | partner | client-result | booked | partner-result
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({ objectives: [] });
  const [partner, setPartner] = useState(initialPartner);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [booking, setBooking] = useState(null);

  const handleBooked = useCallback((b) => {
    setBooking(b);
    setPhase("booked");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ---- Autosave / restore ----
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.phase === "partner") setPhase("partner");
        if (typeof s.stepIdx === "number") setStepIdx(Math.min(s.stepIdx, STEPS.length - 1));
        if (s.answers) setAnswers({ objectives: [], ...s.answers });
        if (s.partner) setPartner({ ...initialPartner, ...s.partner });
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (phase === "client-result" || phase === "booked" || phase === "partner-result") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ phase, stepIdx, answers, partner }));
    } catch {
      /* ignore */
    }
  }, [phase, stepIdx, answers, partner]);

  const resetSaved = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  const currentStep = STEPS[stepIdx];
  const isLastStep = stepIdx === STEPS.length - 1;

  const answerSingle = (key, id) => {
    setAnswers((a) => ({ ...a, [key]: id }));
    if (key === "stage" && id === "partner") {
      setPhase("partner");
      return;
    }
    // Single-choice answers advance automatically — one tap per question.
    if (!isLastStep) setStepIdx((i) => i + 1);
  };
  const toggleMulti = (key, id) =>
    setAnswers((a) => {
      const list = a[key] || [];
      return { ...a, [key]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] };
    });

  const canProceedStep = () => {
    if (currentStep.type === "multi") {
      const val = answers[currentStep.key];
      return Array.isArray(val) && val.length > 0;
    }
    return !!answers[currentStep.key];
  };

  const nextStep = () => !isLastStep && setStepIdx((i) => i + 1);
  const prevStep = () => stepIdx > 0 && setStepIdx((i) => i - 1);

  const submitClient = async () => {
    if (!canProceedStep()) return;
    setSubmitting(true);
    try {
      const payload = {
        pathway: answers.stage,
        stage: answers.stage,
        objectives: answers.objectives || [],
        timing: answers.timing,
        ...getAttribution(),
      };
      const { data } = await api.post("/diagnostic", payload);
      setResult(data);
      setPhase("client-result");
      resetSaved();
      trackConversion("diagnostic-submitted", data?.recommended_product);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Partner validation ----
  const validatePartner = () => {
    const e = {};
    if (!partner.company_name.trim()) e.company_name = "Required";
    if (!partner.contact_name.trim()) e.contact_name = "Required";
    if (!partner.email.trim()) e.email = "Required";
    else if (!isEmail(partner.email.trim())) e.email = "Enter a valid email";
    if (partner.website && !isUrl(partner.website)) e.website = "Enter a valid URL";
    if (partner.phone && !isValidPhoneNumber(partner.phone)) e.phone = "Enter a valid international phone";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitPartner = async (ev) => {
    ev.preventDefault();
    if (!validatePartner()) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/partner-enquiry", { ...partner, ...getAttribution() });
      setPhase("partner-result");
      resetSaved();
      trackConversion("partner-enquiry-submitted");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const bookingNotes = result
    ? [
        "Saudi Entry Diagnostic",
        `Stage: ${labelFor("stage", answers.stage)}`,
        `Needs: ${(answers.objectives || []).map((o) => labelFor("objectives", o)).join(", ")}`,
        `Timing: ${labelFor("timing", answers.timing)}`,
        `Recommended: ${result.recommended_product}`,
      ].join("\n")
    : "";

  return (
    <div className="bg-cream">
      {/* Compact hero — questions start immediately below */}
      {phase === "client" && (
        <section
          data-testid="diagnostic-hero"
          className="kingdom-fade text-paper py-16 md:py-20 relative overflow-hidden"
        >
          <div className="skyline"></div>
          <div className="ambient-glow"></div>
          <div className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-10">
            <div className="mb-8"><ServiceMarker name="assessment-scope" /></div>
            <span className="eyebrow">Saudi Entry Diagnostic · 3 questions · 30 seconds</span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-[56px] leading-[1.03] mt-6 max-w-3xl">
              Find your right first move <br />
              <span className="italic text-gold">in the Kingdom.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-warm/80 leading-relaxed max-w-2xl font-light">
              Three quick answers, then pick a time on our calendar. You will see
              your recommended first move and book the call in one flow.
            </p>
          </div>
        </section>
      )}

      {/* Client diagnostic — 3 steps */}
      {phase === "client" && (
        <section
          data-testid={`diagnostic-step-${currentStep.key}`}
          className="py-16 md:py-24 min-h-[70vh]"
        >
          <div className="max-w-[900px] mx-auto px-5 md:px-10">
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.24em] text-charcoal/50 mb-5">
              <span data-testid="diagnostic-step-counter">Question {stepIdx + 1} of {STEPS.length}</span>
              <span>{Math.round(((stepIdx + 1) / STEPS.length) * 100)}%</span>
            </div>
            <div className="flex gap-1.5 mb-10">
              {STEPS.map((s, i) => (
                <div
                  key={s.key}
                  className={`step-chip flex-1 ${i === stepIdx ? "is-active" : i < stepIdx ? "is-complete" : ""}`}
                ></div>
              ))}
            </div>

            <div className="diag-step-enter" key={currentStep.key}>
              <h2 className="font-display text-3xl md:text-5xl leading-[1.05] text-charcoal">
                {currentStep.title}
              </h2>
              {currentStep.subtitle && (
                <p className="mt-4 text-sm text-charcoal/60 italic">{currentStep.subtitle}</p>
              )}

              <div className="mt-10 md:mt-12 space-y-3">
                {currentStep.options.map((opt) => {
                  const val = answers[currentStep.key];
                  const isSelected =
                    currentStep.type === "multi"
                      ? Array.isArray(val) && val.includes(opt.id)
                      : val === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() =>
                        currentStep.type === "multi"
                          ? toggleMulti(currentStep.key, opt.id)
                          : answerSingle(currentStep.key, opt.id)
                      }
                      data-testid={`step-${currentStep.key}-${opt.id}`}
                      className={`diag-option ${isSelected ? "is-selected" : ""}`}
                    >
                      <span className="flex items-center justify-between gap-6">
                        <span className="text-left">
                          <span className="block font-display text-base md:text-lg text-charcoal">
                            {opt.label}
                          </span>
                          {opt.sub && (
                            <span className="block text-[13px] text-charcoal/55 mt-1 leading-relaxed">
                              {opt.sub}
                            </span>
                          )}
                        </span>
                        {isSelected ? (
                          <Check size={16} className="text-gold shrink-0" />
                        ) : (
                          <ArrowRight size={14} className="text-charcoal/25 shrink-0" />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-12 md:mt-14 flex items-center justify-between">
              <button
                onClick={prevStep}
                disabled={stepIdx === 0}
                data-testid="diagnostic-back"
                className={`inline-flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-charcoal/60 hover:text-charcoal transition-colors duration-500 ${stepIdx === 0 ? "invisible" : ""}`}
              >
                <ArrowLeft size={14} /> Back
              </button>

              {currentStep.type === "multi" && (
                <button
                  onClick={nextStep}
                  disabled={!canProceedStep()}
                  data-testid="diagnostic-next"
                  className={`btn-gold ${!canProceedStep() ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  Continue
                  <ArrowRight size={16} />
                </button>
              )}

              {isLastStep && (
                <button
                  onClick={submitClient}
                  disabled={!canProceedStep() || submitting}
                  data-testid="diagnostic-submit"
                  className={`btn-gold-lg ${!canProceedStep() ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  {submitting ? "One moment…" : "Show my first move & book a call"}
                  <ArrowRight size={18} />
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Client result — recommendation + calendar in one screen */}
      {phase === "client-result" && result && (
        <section
          data-testid="diagnostic-result"
          className="kingdom-fade text-paper py-20 md:py-28 min-h-[80vh] relative overflow-hidden"
        >
          <div className="skyline"></div>
          <div className="skyline-sunrise"></div>
          <div className="ambient-glow"></div>
          <div className="max-w-[1000px] mx-auto px-5 md:px-10 relative z-10 diag-step-enter">
            <span className="eyebrow">Your first move</span>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05] mt-6">
              {result.result_headline.split(":")[0]}
              {result.result_headline.includes(":") && (
                <>
                  :<br />
                  <span className="italic text-gold">{result.result_headline.split(":")[1].trim()}</span>
                </>
              )}
            </h2>
            <p className="mt-6 text-base md:text-lg text-warm/80 leading-relaxed max-w-2xl font-light">
              {result.result_body}
            </p>

            <div className="mt-12">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <span className="eyebrow">Book your call</span>
                  <h3 className="font-display text-2xl md:text-3xl mt-3 text-paper">
                    Pick a time. 30 minutes with HuddleUp.
                  </h3>
                </div>
                <a
                  href={result.cal_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="result-book-call-fallback"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-warm/60 hover:text-gold transition-colors duration-500"
                >
                  Open in a new tab
                  <ArrowRight size={12} />
                </a>
              </div>
              <div
                data-testid="result-cal-embed-wrapper"
                className="mt-6 bg-obsidian/60 border border-gold/20 p-2 md:p-4"
              >
                <CalEmbed
                  calLink={
                    (result.cal_link || "https://cal.com/huddleup/30min")
                      .replace(/^https?:\/\/cal\.com\//, "")
                  }
                  product={result?.recommended_product}
                  notes={bookingNotes}
                  onBooked={handleBooked}
                />
              </div>
            </div>

            <p className="mt-8 text-xs text-warm/50 tracking-[0.2em] uppercase">
              Your answers travel with the booking so we arrive prepared.
            </p>
          </div>
          <SkylineDivider variant="gold" opacity={0.22} height={160} className="skyline-bottom" />
        </section>
      )}

      {/* Booking confirmed */}
      {phase === "booked" && (
        <BookingConfirmation booking={booking} product={result?.recommended_product} />
      )}

      {/* Partner enquiry */}
      {phase === "partner" && (
        <section data-testid="partner-enquiry-form" className="py-20 md:py-28">
          <div className="max-w-[900px] mx-auto px-5 md:px-10">
            <span className="eyebrow eyebrow-dark">Partnership Enquiry</span>
            <h2 className="font-display text-3xl md:text-5xl leading-[1.05] mt-6 text-charcoal">
              Partnership enquiry.
            </h2>
            <p className="mt-6 text-charcoal/70 max-w-2xl leading-relaxed">
              HuddleUp works with selected providers, advisors, and ecosystem partners who can support international companies entering the Kingdom.
            </p>

            <form onSubmit={submitPartner} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-x-8 md:gap-y-10">
              <Field label="Company name" required error={errors.company_name}>
                <TextInput testId="partner-company" value={partner.company_name} onChange={(v) => setPartner({ ...partner, company_name: v })} invalid={!!errors.company_name} />
              </Field>
              <Field label="Website" error={errors.website}>
                <TextInput testId="partner-website" value={partner.website} onChange={(v) => setPartner({ ...partner, website: v })} invalid={!!errors.website} placeholder="company.com" />
              </Field>
              <Field label="Contact name" required error={errors.contact_name}>
                <TextInput testId="partner-name" value={partner.contact_name} onChange={(v) => setPartner({ ...partner, contact_name: v })} invalid={!!errors.contact_name} />
              </Field>
              <Field label="Email" required error={errors.email}>
                <TextInput testId="partner-email" type="email" value={partner.email} onChange={(v) => setPartner({ ...partner, email: v })} invalid={!!errors.email} />
              </Field>
              <Field label="Phone or WhatsApp" error={errors.phone}>
                <PhoneField
                  testId="partner-phone"
                  value={partner.phone}
                  country={partner.phone_country}
                  invalid={!!errors.phone}
                  onValueChange={({ value, country, countryCode, countryName }) =>
                    setPartner({
                      ...partner,
                      phone: value,
                      phone_country: country,
                      phone_country_name: countryName,
                      phone_country_code: countryCode,
                    })
                  }
                />
              </Field>
              <Field label="Country">
                <CountryField
                  testId="partner-country"
                  value={partner.country}
                  onChange={({ code, name }) => setPartner({ ...partner, country: code, country_name: name })}
                />
              </Field>
              <Field label="What type of partner are you?">
                <TextInput testId="partner-type" value={partner.partner_type} onChange={(v) => setPartner({ ...partner, partner_type: v })} />
              </Field>
              <Field label="What services do you provide?">
                <TextInput testId="partner-services" value={partner.services} onChange={(v) => setPartner({ ...partner, services: v })} />
              </Field>
              <Field label="Which markets do you cover?">
                <TextInput testId="partner-markets" value={partner.markets} onChange={(v) => setPartner({ ...partner, markets: v })} />
              </Field>
              <Field label="Do you already support Saudi market entry?">
                <TextInput testId="partner-supports-saudi" value={partner.supports_saudi} onChange={(v) => setPartner({ ...partner, supports_saudi: v })} />
              </Field>
              <div className="md:col-span-2">
                <Field label="Anything else we should know?" optional>
                  <TextAreaInput testId="partner-notes" value={partner.notes} onChange={(v) => setPartner({ ...partner, notes: v })} />
                </Field>
              </div>

              <div className="md:col-span-2 mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-6">
                <button
                  type="button"
                  onClick={() => { setAnswers((a) => ({ ...a, stage: undefined })); setPhase("client"); }}
                  data-testid="partner-back"
                  className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-charcoal/60 hover:text-charcoal transition-colors duration-500"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <button type="submit" disabled={submitting} data-testid="partner-submit" className="btn-gold-lg">
                  {submitting ? "Submitting…" : "Submit partnership enquiry"}
                  <ArrowRight size={18} />
                </button>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* Partner result */}
      {phase === "partner-result" && (
        <section
          data-testid="partner-result"
          className="kingdom-fade text-paper py-24 md:py-32 min-h-[70vh] relative overflow-hidden"
        >
          <div className="skyline"></div>
          <div className="ambient-glow"></div>
          <div className="max-w-[900px] mx-auto px-5 md:px-10 relative z-10 diag-step-enter">
            <span className="eyebrow">Thank you</span>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05] mt-8">
              Thank you. <br />
              <span className="italic text-gold">We’ll review the fit.</span>
            </h2>
            <p className="mt-10 text-lg text-warm/85 leading-relaxed max-w-2xl font-light">
              Your details have been sent to HuddleUp. If there is a relevant partnership fit, we’ll be in touch.
            </p>
          </div>
          <SkylineDivider variant="gold" opacity={0.22} height={160} className="skyline-bottom" />
        </section>
      )}

      {/* FAQ */}
      {phase !== "client-result" && phase !== "booked" && phase !== "partner-result" && (
        <section data-testid="diagnostic-faq" className="pb-24 md:pb-32 pt-6">
          <div className="max-w-[1100px] mx-auto px-5 md:px-10">
            <div className="hairline-dark mb-16"></div>
            <span className="eyebrow eyebrow-dark">Common Questions</span>
            <h3 className="font-display text-3xl md:text-4xl leading-[1.05] mt-6 text-charcoal">
              Before you enter <span className="italic text-gold">the Kingdom.</span>
            </h3>
            <div className="mt-12">
              <FAQ items={FAQS} testIdPrefix="diagnostic-faq" />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// -------- Form field primitives ---------

function Field({ label, required, optional, error, help, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.22em] text-charcoal/60 mb-2 inline-flex items-center gap-2">
        {label}
        {required && <span className="text-gold">*</span>}
        {optional && <span className="text-charcoal/35 normal-case tracking-normal text-xs">(optional)</span>}
      </span>
      {children}
      {help && !error && <span className="mt-1.5 block text-[11px] text-charcoal/45">{help}</span>}
      {error && <span className="mt-1.5 block text-[12px] text-red-600">{error}</span>}
    </label>
  );
}

function TextInput({ testId, value, onChange, type = "text", placeholder, invalid }) {
  return (
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full bg-transparent border-b pb-3 pt-1 font-display text-lg text-charcoal outline-none transition-colors duration-500 placeholder-charcoal/25 ${
        invalid ? "border-red-500" : "border-charcoal/25 focus:border-gold"
      }`}
    />
  );
}

function TextAreaInput({ testId, value, onChange }) {
  return (
    <textarea
      data-testid={testId}
      rows={4}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border border-charcoal/20 p-4 font-sans text-[15px] text-charcoal outline-none focus:border-gold transition-colors duration-500 resize-none"
    />
  );
}
